import { AppColors, AppPalette, CustomPalette } from "@bpipartners/webui";

const appPalette: AppPalette = {
  primary: {
    "0": "#000000",
    "5": "#000412",
    "10": "#000924",
    "20": "#040E2B",
    "25": "#071332",
    "30": "#091739",
    "35": "#0C1B40",
    "40": "#0E1F47",
    "50": "#121A39",
    "60": "#132D45",
    "70": "#133951",
    "80": "#134562",
    "90": "#427291",
    "95": "#71A0C0",
    "98": "#A0CFEF",
    // "99": "#D0E7F7",
    "99": "#E1EBF1",
    "100": "#FFFFFF",
  },
  secondary: {
    "0": "#000000",
    "5": "#001510",
    "10": "#002A20",
    "20": "#004434",
    "25": "#00533F",
    "30": "#00624A",
    "35": "#007156",
    "40": "#008062",
    "50": "#009475",
    "60": "#17A080",
    "70": "#23A98B",
    "80": "#2FB196",
    "90": "#59C3AB",
    "95": "#84D5C0",
    "98": "#AFEBD5",
    "99": "#D7F5EA",
    "100": "#FFFFFF",
  },
  tertiary: {
    "0": "#000000",
    "5": "#2B0A00",
    "10": "#3F1400",
    "20": "#662300",
    "25": "#7C2B00",
    "30": "#923300",
    "35": "#A83B00",
    "40": "#BE4300",
    "50": "#E46300",
    "60": "#FF7D1A",
    "70": "#FF9743",
    "80": "#FFB16D",
    "90": "#FFCB98",
    "95": "#FFE0C0",
    "98": "#FFF1E5",
    "99": "#FFF7F2",
    "100": "#FFFFFF",
  },
  neutral: {
    "0": "#000000",
    "5": "#0F0F0F",
    "10": "#1A1A1A",
    "20": "#2E2E2E",
    "25": "#383838",
    "30": "#424242",
    "35": "#4D4D4D",
    "40": "#575757",
    "50": "#717171",
    "60": "#8C8C8C",
    "70": "#A6A6A6",
    "80": "#C1C1C1",
    "90": "#DBDBDB",
    "95": "#EDEDED",
    "98": "#F8F8F8",
    "99": "#FCFCFC",
    "100": "#FFFFFF",
  },
  neutralVariant: {
    "0": "#000000",
    "5": "#0E1013",
    "10": "#191C20",
    "20": "#2E3135",
    "25": "#383B3F",
    "30": "#434649",
    "35": "#4E5154",
    "40": "#595C5F",
    "50": "#717376",
    "60": "#8A8C8F",
    "70": "#A4A6A9",
    "80": "#BFC1C3",
    "90": "#DADCDE",
    "95": "#ECEEF0",
    "98": "#F7F8F9",
    "99": "#FCFCFD",
    "100": "#FFFFFF",
  },
  error: {
    "0": "#000000",
    "5": "#2D0001",
    "10": "#410002",
    "20": "#690005",
    "25": "#7E0007",
    "30": "#93000A",
    "35": "#A80710",
    "40": "#BA1A1A",
    "50": "#DE3730",
    "60": "#FF5449",
    "70": "#FF897D",
    "80": "#FFB4AB",
    "90": "#FFDAD6",
    "95": "#FFEDEA",
    "98": "#FFF8F7",
    "99": "#FFFBFF",
    "100": "#FFFFFF",
  },
};

const appColors: AppColors = {
  primary: appPalette.primary[50],
  onPrimary: appPalette.primary[100],
  primaryContainer: appPalette.primary[90],
  onPrimaryContainer: appPalette.primary[100],
  secondary: appPalette.secondary[50],
  onSecondary: appPalette.secondary[100],
  secondaryContainer: appPalette.secondary[90],
  onSecondaryContainer: appPalette.secondary[10],
  tertiary: appPalette.tertiary[50],
  onTertiary: appPalette.tertiary[100],
  tertiaryContainer: appPalette.tertiary[90],
  onTertiaryContainer: appPalette.tertiary[10],
  error: appPalette.error[40],
  onError: appPalette.error[100],
  errorContainer: appPalette.error[90],
  onErrorContainer: appPalette.error[10],
  inversePrimary: appPalette.primary[80],
  primaryFixed: appPalette.primary[90],
  onPrimaryFixed: appPalette.primary[10],
  primaryFixedDim: appPalette.primary[80],
  onPrimaryFixedVariant: appPalette.primary[30],
  secondaryFixed: appPalette.secondary[90],
  onSecondaryFixed: appPalette.secondary[10],
  secondaryFixedDim: appPalette.secondary[80],
  onSecondaryFixedVariant: appPalette.secondary[30],
  tertiaryFixed: appPalette.tertiary[90],
  onTertiaryFixed: appPalette.tertiary[10],
  tertiaryFixedDim: appPalette.tertiary[80],
  onTertiaryFixedVariant: appPalette.tertiary[30],
  background: appPalette.neutral[99],
  onBackground: appPalette.neutral[5],
  outline: appPalette.neutral[50],
  outlineVariant: appPalette.neutral[80],
  shadow: appPalette.primary[0],
  scrim: appPalette.primary[0],
  inverseSurface: appPalette.neutralVariant[90],
  inverseOnSurface: appPalette.neutralVariant[10],
  surface: appPalette.neutral[95],
  onSurface: appPalette.neutral[10],
  surfaceVariant: appPalette.neutralVariant[95],
  onSurfaceVariant: appPalette.neutralVariant[10],
  surfaceDim: appPalette.neutral[70],
  surfaceBright: appPalette.neutral[100],
  surfaceContainerLowest: appPalette.neutral[98],
  surfaceContainerLow: appPalette.neutral[95],
  surfaceContainer: appPalette.neutral[90],
  surfaceContainerHigh: appPalette.neutral[80],
  surfaceContainerHighest: appPalette.neutral[70],
  backdrop: appPalette.primary[30],

  relation: { organization: "#0B79D0", person: "#C77700", tag: "#C51162" },
  planning: {},

  importance: {
    major: { fg: "#d82e18", bg: "#f2bdb4" },
    medium: { fg: "#db7c2e", bg: "#EAAF71" },
    normal: { fg: "#6b6f73", bg: "#edf1f4" },
    low: { fg: "#01a2fa", bg: "#c6e9ff" },
  },
  surfaceTint: "",
  timesheet: {
    green: "",
    orange: "",
  },
  customColors: {
    raportTableColors: {
      domain: appPalette.secondary[95],
      goal: appPalette.secondary[98],
      measurement: appPalette.secondary[99],
    },
    measurementLevels: {
      1: "#d82e18",
      2: "#db7c2e",
      3: "#CCA400",
      4: "#8FB302",
      5: "#3D9901",
    },
  },
};

export const colors: CustomPalette = {
  appColors,
  appPalette,
  palette: {
    background: {
      default: appColors.background,
      paper: appColors.surfaceBright,
    },
    primary: {
      light: appColors.primary,
      main: appPalette.primary[60],
      dark: appColors.primary,
      contrastText: appColors.onPrimary,
    },
    secondary: {
      light: appColors.secondary,
      main: appPalette.secondary[60],
      dark: appColors.secondary,
      contrastText: appColors.onSecondary,
    },
  },
};
